import React, { useMemo, useState } from "react";
import { ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTable, clientSortRows } from "../components/ui";
import PriceDisplay from "../components/PriceDisplay";

/**
 * Figma Gain cell (1:2949): Inter SemiBold 12px + 24px circular badge.
 * Up = green circle + white arrow rotated 180°; down = red circle + white arrow.
 * Negative label keeps the space: "- 1.2%"
 */
const GainCell = ({ gain }) => {
  const isUp = gain > 0;
  const isFlat = gain === 0;
  const dir = isFlat ? "flat" : isUp ? "up" : "down";
  const abs = Math.abs(gain);
  const label = isFlat
    ? `${abs.toFixed(1)}%`
    : isUp
      ? `+${abs.toFixed(1)}%`
      : `- ${abs.toFixed(1)}%`;

  return (
    <span className={`oh-dash-gain oh-dash-gain--${dir}`}>
      <span className="oh-dash-gain__value">{label}</span>
      {!isFlat && (
        <span
          className={`oh-dash-gain__badge${isUp ? " oh-dash-gain__badge--up" : ""}`}
          aria-hidden="true"
        >
          <ArrowDown size={14} strokeWidth={2.75} color="#FFFFFF" />
        </span>
      )}
    </span>
  );
};

const TopProductsTable = ({ topProducts }) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const [tableSortColumn, setTableSortColumn] = useState("orders");
  const [sortDirection, setSortDirection] = useState("desc");
  const rows = Array.isArray(topProducts) ? topProducts : [];
  const fmtName = (p) =>
    (isArabic
      ? p?.name_ar || p?.name || p?.name_en
      : p?.name_en || p?.name || p?.name_ar) || "-";
  const num = (v) => (typeof v === "number" ? v : Number(v) || 0);
  const nf = (v) => num(v).toLocaleString(isArabic ? "ar" : undefined);

  const sortedRows = useMemo(
    () =>
      clientSortRows(rows, tableSortColumn, sortDirection, (product, col) => {
        switch (col) {
          case "name":
            return fmtName(product).toLowerCase();
          case "orders":
            return num(product.orders);
          case "revenue":
            return num(product.revenue);
          case "gain":
            return num(product.gain);
          default:
            return "";
        }
      }),
    [rows, tableSortColumn, sortDirection, isArabic]
  );

  const rankByKey = useMemo(() => {
    const map = new Map();
    sortedRows.forEach((product, index) => {
      map.set(product?._id ?? index, index + 1);
    });
    return map;
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "index",
        header: t("dashboardPage.topProducts.index") || "#",
        sortable: false,
        hideable: false,
        nowrap: true,
        width: 52,
        minWidth: 52,
        render: (product, index) => (
          <span className="oh-dash-cell oh-dash-index">
            {rankByKey.get(product?._id ?? index) ?? index + 1}
          </span>
        ),
      },
      {
        id: "name",
        header: t("dashboardPage.topProducts.productName"),
        sortable: false,
        strong: true,
        width: "auto",
        minWidth: 0,
        render: (product) => (
          <span className="oh-dash-cell oh-dash-cell--name">
            {fmtName(product)}
          </span>
        ),
      },
      {
        id: "orders",
        header: t("dashboardPage.topProducts.orders"),
        sortable: true,
        width: "auto",
        minWidth: 0,
        render: (product) => (
          <span className="oh-dash-cell">{nf(product.orders)}</span>
        ),
      },
      {
        id: "revenue",
        header: t("dashboardPage.topProducts.revenue"),
        sortable: true,
        width: "auto",
        minWidth: 0,
        render: (product) => (
          <PriceDisplay
            value={num(product.revenue)}
            iconSize={18}
            color="#030f1b"
            decimals={0}
          />
        ),
      },
      {
        id: "gain",
        header: t("dashboardPage.topProducts.gain"),
        sortable: true,
        width: "auto",
        minWidth: 0,
        render: (product) => <GainCell gain={num(product.gain)} />,
      },
    ],
    [t, isArabic, rankByKey]
  );

  return (
    <div dir={isArabic ? "rtl" : undefined}>
      <DataTable
        tableId="top-products"
        className="oh-dashboard-table"
        title={
          t("dashboardPage.topProducts.topPerforming") ||
          (isArabic ? "الأفضل أداءً" : "Top Performing")
        }
        columns={columns}
        data={sortedRows}
        rowKey="_id"
        emptyState={t("dashboardPage.topProducts.empty")}
        isRtl={isArabic}
        selectAllLabel={t("Select All", { defaultValue: "Select All" })}
        sort={{
          column: tableSortColumn,
          direction: sortDirection,
          onChange: ({ column, direction }) => {
            setTableSortColumn(column);
            setSortDirection(direction);
          },
        }}
        pagination={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showLabel: t("show"),
          ofLabel: t("of", { defaultValue: "Of" }),
          resultsLabel: t("results", { defaultValue: "results" }),
          isRtl: isArabic,
          previousLabel: t("previous"),
          nextLabel: t("next"),
        }}
      />
    </div>
  );
};

export default TopProductsTable;
